import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URLS } from '../config/api'; 

const PaymentResponse = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('');
  const [message, setMessage] = useState('Processing payment...');
  const [loading, setLoading] = useState(true);
  const [receiptData, setReceiptData] = useState(null);
  const [transactionData, setTransactionData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // ✅ Extract query parameters
        const qp = Object.fromEntries([...searchParams.entries()]);
        // console.log('🔍 Looking for merchantTxnId in:', qp.merchantTxnId, qp.txnId, qp.atomTokenId);

        //const status = extractedData.paymentStatus;
        // const redirectUrl = `http://localhost:5000/payment/response?merchantTxnId=${encodeURIComponent(merchTxnId)}&status=${encodeURIComponent(status)}
        // &amount=${encodeURIComponent(amount)}&customerName=${encodeURIComponent(customerName)}&customerMobile=${encodeURIComponent(customerMobile)}`
        
        // ✅ Initialize transaction data variable
        let transactionInfo = {};
        let paymentSuccessful = false;

        // ✅ Check for merchantTxnId and fetch from database
        const merchantTxnId = qp.merchantTxnId;
        
        if (!merchantTxnId) {
          setStatus('failed');
          setMessage('No transaction ID found');
          setLoading(false);
          return;
        }
        
        try {
          const poll = async (attempts = 0) => {
            try {
              const response = await axios.get(`/api/playerNew/receipt-by-txn/${merchantTxnId}`);
              const paymentData = response.data?.data;
              
              if (!paymentData && attempts < 15) {
                // console.log(`⏳ Payment data not found, retrying... (${attempts + 1}/10)`);
                return setTimeout(() => poll(attempts + 1), 1500);
              }
              
              if (!paymentData) {
                setStatus('pending');
                setMessage('');
                setLoading(false);
                return;
              }
              
              // ✅ Check success based on response_code and response_message from NTT Data
              const isSuccess = paymentData.payment_status.toUpperCase() === 'SUCCESS';
              
              // ✅ Set transaction data from database
              transactionInfo = {
                merchTxnId: paymentData.transactionId,
                atomTxnId: paymentData.atomTxnId || 'N/A',
                bankTxnId: paymentData.bankTxnId || 'N/A',
                paymentMode: paymentData.payment_mode || 'UPI',
                amount: Number(paymentData.registrationFee) || 0
              };
              
              setTransactionData(transactionInfo);
              
              if (isSuccess) {
                paymentSuccessful = true;
                // console.log('✅ Payment verified from database:', transactionInfo);
                
                // ✅ Fetch player registration data by merchant transaction ID
                try {
                   setReceiptData({
                      receiptNumber: paymentData.receipt_number || `KNKS-${Date.now()}`,
                      playerName: paymentData.name || qp.customerName || 'N/A',
                      playerMobile: paymentData.phone || qp.customerMobile || 'N/A',
                      amount: paymentData.registrationFee || transactionInfo.amount || 0,
                      paymentDate: paymentData.payment_date || new Date().toISOString(),
                      transactionId: paymentData.transactionId,
                      atomTxnId: paymentData.atomTxnId,
                      bankTxnId: paymentData.bankTxnId,
                      paymentMode: paymentData.payment_mode || transactionInfo.paymentMode
                    });
                    
                    setStatus('success');
                    setMessage('Payment successful! Registration completed. 🎉');
                    setLoading(false);
                 
                } catch (playerErr) {
                  console.error('⚠️ Player data fetch error:', playerErr.message);
                  // Continue even if player data not found
                  setStatus('success');
                  setMessage('Payment successful!');
                  setLoading(false);
                }
                
              } else {
                setStatus('failed');
                setMessage(paymentData.response_message || 'Payment failed');
                setLoading(false);
                return;
              }
              
            } catch (error) {
              if (attempts < 10) {
                // console.log(`⚠️ Database fetch failed, retrying... (${attempts + 1}/10)`);
                return setTimeout(() => poll(attempts + 1), 1500);
              } else {
                setStatus('failed');
                setMessage('Unable to verify payment status. Please contact support.');
                setLoading(false);
                return;
              }
            }
          };
          
          await poll();
          
        } catch (error) {
          // console.error('❌ Payment verification error:', error);
          setStatus('failed');
          setMessage('Payment verification failed');
          setLoading(false);
          return;
        }        
      } catch (err) {
        // console.error('❌ Payment response processing error:', err);
        setStatus('failed');
        setMessage('Registration failed due to an error.');
      } finally {
        setLoading(false);
      }
    })();
  }, [searchParams]);

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return (
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'failed':
        return (
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      case 'cancelled':
        return (
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636" />
            </svg>
          </div>
        );
      case 'timeout':
        return (
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'pending':
        return (
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'cancelled':
        return 'text-orange-600';
      case 'timeout':
        return 'text-purple-600';
      case 'pending':
        return 'text-blue-600';
      default:
        return 'text-yellow-600';
    }
  };

  const printReceipt = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Kabir Nagar Khel Samiti Payment Receipt</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: #f8f9fa;
            }
            .receipt { 
              background: white;
              border: 2px solid #e2e8f0; 
              border-radius: 12px;
              padding: 30px; 
              max-width: 450px; 
              margin: 0 auto;
              box-shadow: 0 10px 25px rgba(0,0,0,0.1);
            }
            .header { 
              text-align: center; 
              border-bottom: 2px solid #e2e8f0; 
              padding-bottom: 20px; 
              margin-bottom: 25px; 
            }
            .logo-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              margin-bottom: 20px;
            }
            .logo-wrapper {
              width: 70px;
              height: 70px;
              background: linear-gradient(135deg, #f97316, #dc2626);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 4px 15px rgba(249, 115, 22, 0.3);
              margin-right: 20px;
              overflow: hidden;
              position: relative;
            }
            .logo-wrapper img {
              width: 100%;
              height: 100%;
              object-fit: contain;
              border-radius: 50%;
            }
            .org-info {
              text-align: center;
              flex: 1;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .org-info h2 {
              margin: 0;
              font-size: 22px;
              font-weight: 700;
              color: #1f2937;
              line-height: 1.2;
            }
            .org-info .location {
              margin: 5px 0 0 0;
              font-size: 14px;
              font-weight: 600;
              color: #6b7280;
            }
            .receipt-title {
              font-size: 16px;
              font-weight: 600;
              color: #374151;
              margin-top: 15px;
            }
            .row { 
              display: flex; 
              justify-content: space-between; 
              margin: 12px 0; 
              padding: 8px 0;
              border-bottom: 1px solid #f3f4f6;
            }
            .row:last-child {
              border-bottom: none;
            }
            .row span:first-child {
              font-weight: 600;
              color: #4b5563;
            }
            .row span:last-child {
              font-weight: 500;
              color: #1f2937;
            }
            .total { 
              border-top: 2px solid #e2e8f0; 
              padding-top: 15px; 
              margin-top: 20px; 
              font-weight: bold;
              background: #f8f9fa;
              padding: 15px;
              border-radius: 8px;
            }
            .total .row {
              font-size: 18px;
              color: #1f2937;
            }
            .footer {
              text-align: center;
              margin-top: 25px;
              padding-top: 20px;
              border-top: 1px solid #e2e8f0;
              color: #6b7280;
              font-style: italic;
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="header">
              <div class="logo-container">
                <div class="logo-wrapper">
                  <img src="/mylogo.png" alt="KNKSS Logo" onerror="this.src='/mylogo.JPG';" />
                </div>
                <div class="org-info">
                  <h2>Kabir Nagar Khel Samiti</h2>
                  <p class="location">Raipur</p>
                  <p class="receipt-title">Payment Receipt</p>
                </div>
              </div>
            </div>
            <div class="row">
              <span>Receipt No:</span>
              <span>${receiptData?.receiptNumber || 'N/A'}</span>
            </div>
            <div class="row">
              <span>Date:</span>
              <span>${new Date(receiptData?.paymentDate).toLocaleDateString()}</span>
            </div>
            <div class="row">
              <span>Player Name:</span>
              <span>${receiptData?.playerName || 'N/A'}</span>
            </div>
            <div class="row">
              <span>Mobile:</span>
              <span>${receiptData?.playerMobile || 'N/A'}</span>
            </div>
            <div class="total">
              <div class="row">
                <span>Amount:</span>
                <span>₹${receiptData?.amount || 0}</span>
              </div>
            </div>
            <div class="footer">
              <p>Thank you for your payment!</p>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        {getStatusIcon()}
        
        <div className="text-center">
          <h1 className={`text-2xl font-bold mb-2 ${getStatusColor()}`}>
            {status === 'success' && 'Payment Successful!'}
            {status === 'failed' && 'Payment Failed'}
            {status === 'cancelled' && 'Payment Cancelled'}
            {status === 'timeout' && 'Payment Timeout'}
            {status === 'pending' && 'Payment Pending'}
            {status === 'unknown' && 'Payment Status Unknown'}
          </h1>
          
          <p className="text-gray-600 mb-6">{message}</p>
          
          {/* Enhanced Transaction Data Display */}
          {transactionData && (
            <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">🏦 Transaction Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-600">Merchant Txn ID:</span>
                  <span className="font-mono text-blue-800 break-all">{transactionData.merchTxnId}</span>
                </div>
                {transactionData.atomTxnId && transactionData.atomTxnId !== 'N/A' && (
                  <div className="flex justify-between">
                    <span className="text-blue-600">Atom Txn ID:</span>
                    <span className="font-mono text-blue-800 break-all">{transactionData.atomTxnId}</span>
                  </div>
                )}
                {transactionData.bankTxnId && transactionData.bankTxnId !== 'N/A' && (
                  <div className="flex justify-between">
                    <span className="text-blue-600">Bank Txn ID:</span>
                    <span className="font-mono text-blue-800 break-all">{transactionData.bankTxnId}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-blue-600">Amount:</span>
                  <span className="font-bold text-green-600 text-lg">₹{transactionData.amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Payment Mode:</span>
                  <span className="font-semibold text-blue-800">{transactionData.paymentMode}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Status:</span>
                  <span className={`font-semibold px-2 py-1 rounded text-xs ${
                    status === 'success' ? 'bg-green-100 text-green-800' : 
                    status === 'failed' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {status.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Timestamp:</span>
                  <span className="text-blue-800">{new Date().toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Query Parameters Debug Info */}
          {Object.keys(Object.fromEntries([...searchParams.entries()])).length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-3">🔍 Raw Payment Data</h3>
              <div className="space-y-1 text-xs">
                {Object.entries(Object.fromEntries([...searchParams.entries()])).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-600 font-mono">{key}:</span>
                    <span className="text-gray-800 font-mono break-all max-w-xs">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {receiptData && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
              <div className="text-center border-b-2 border-gray-100 pb-6 mb-6">
                <div className="flex flex-col items-center justify-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center shadow-lg overflow-hidden relative mb-4">
                    <img 
                      src="/mylogo.png" 
                      alt="KNKSS Logo" 
                      className="w-full h-full object-contain rounded-full"
                      onError={(e) => {
                        if (e.target.src.includes('.png')) {
                          e.target.src = '/mylogo.JPG';
                        }
                      }}
                    />
                  </div>
                  <div className="text-center flex flex-col items-center">
                    <h3 className="text-2xl font-bold text-gray-900 leading-tight">Kabir Nagar Khel Samiti</h3>
                    <p className="text-base font-semibold text-gray-600 mt-1">Raipur</p>
                    <p className="text-lg font-semibold text-gray-700 mt-2">Payment Receipt</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600 font-semibold">Receipt No:</span>
                  <span className="font-mono text-gray-900 font-medium">{receiptData.receiptNumber}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600 font-semibold">Player:</span>
                  <span className="text-gray-900 font-medium">{receiptData.playerName}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600 font-semibold">Mobile:</span>
                  <span className="text-gray-900 font-medium">{receiptData.playerMobile}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mt-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-bold text-lg">Amount:</span>
                    <span className="text-2xl font-bold text-gray-900">₹{receiptData.amount}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={printReceipt}
                className="w-full mt-3 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Print Receipt
              </button>
            </div>
          )}
          
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Go to Home
            </button>
            
            {(status === 'failed' || status === 'cancelled' || status === 'timeout') && (
              <button
                onClick={() => window.location.href = '/register'}
                className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default PaymentResponse;